// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, Container, Divider } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialMainGroupLedgerState,
    MainGroupLedgerSchema,
    MainGroupLedgerFormFields,
} from "./Configuration.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";
import MainGroupLedgerAccounts from "./MainGroupLedgerAccounts";

const MainGroupLedger = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [main_group_ledger, setMainGroupLedger] = useState(InitialMainGroupLedgerState);
    const [formFields, setFormFields] = useState(MainGroupLedgerFormFields);
    const [mainGroupLedgerAccounts, setMainGroupLedgerAccounts] = useState([]);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && field.type === "select"
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const getSubAccounts = async () => {
        spinService.on();
        const response = await get(EntityConstants.SUB_ACCOUNTS, "max=all");
        const data = await response.json();
        attachOptions("sub_account_id", data);
        spinService.off();
    };

    const getMainGroupLedger = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.MAIN_GROUP_LEDGERS + `/${id}`);
        const data = await response.json();
        setMainGroupLedger(data);
        setMainGroupLedgerAccounts(data.main_group_ledger_accounts);
        spinService.off();
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const isNewMainGroupLedger = values.id === undefined;
        const url = isNewMainGroupLedger ? EntityConstants.MAIN_GROUP_LEDGERS : EntityConstants.MAIN_GROUP_LEDGERS + `/${values.id}`;
        const method = isNewMainGroupLedger ? post : put;
        const response = await method(url, { main_group_ledger: values });
        if ([200, 201].indexOf(response.status) > -1) {
            toast.success(isNewMainGroupLedger ? MessageContants.MAIN_GROUP_LEDGER_CREATED : MessageContants.MAIN_GROUP_LEDGER_UPDATED);
            onClose();
        }
        spinService.off();
    };

    useEffect(() => {
        getSubAccounts();
        if (id !== undefined) {
            getMainGroupLedger(Number(id));
        }
    }, [id]);

    return (
        <Container>
            <Formik
                enableReinitialize
                initialValues={main_group_ledger}
                validationSchema={MainGroupLedgerSchema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({
                    values,
                    touched,
                    errors,
                    dirty,
                    isSubmitting,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                maxWidth="md"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={handleChange}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {((values.id === undefined && permissions.has(PermissionConstants.MAIN_GROUP_LEDGER_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.MAIN_GROUP_LEDGER_UPDATE))) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:save-fill" />}
                                        type="submit"
                                    >
                                        {values.id === undefined ? 'Submit' : 'Update'}
                                    </Button>
                                }
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <Divider />
            <br />
            {id !== undefined && <MainGroupLedgerAccounts mainGroupLedger={main_group_ledger} mainGroupLedgerId={id} mainGroupLedgerAccounts={mainGroupLedgerAccounts} reload={() => getMainGroupLedger(Number(id))} />}
        </Container>
    );
};

MainGroupLedger.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default MainGroupLedger;
